import { useState, useRef, useEffect } from 'react';
import {
  X,
  Eye,
  ArrowLeft,
  ArrowRight,
  Copy,
  Trash2,
  Plus,
  Image as ImageIcon,
  HelpCircle,
  CheckCheck,
  Timer,
  Trophy,
  ChevronDown,
  CheckCircle2,
  Circle as CircleIcon,
  Square,
  Triangle,
  Diamond,
  Check,
} from 'lucide-react';
import { createQuiz, updateQuiz } from '../../services/quizService';
import { isValidQuiz } from '../../utils/validators';
import { uploadQuizImages, validateImageFile } from '../../services/imageService';
import { getAnswerConfig, getAnswerCount } from '../../utils/answerConfig';
import ImageCropModal from '../common/ImageCropModal';

const LUCIDE_BY_NAME = {
  circle: CircleIcon,
  square: Square,
  triangle: Triangle,
  diamond: Diamond,
  check: Check,
  x: X,
};

const createEmptyQuestion = (type = 'quiz') => {
  if (type === 'truefalse') {
    return {
      type: 'truefalse',
      text: '',
      imageUrl: null,
      answers: [
        { text: 'Sant', isCorrect: true },
        { text: 'Usant', isCorrect: false },
      ],
      timeLimit: 20,
    };
  }
  return {
    type: 'quiz',
    text: '',
    imageUrl: null,
    answers: [
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
    ],
    timeLimit: 20,
  };
};

export default function QuizCreator({ onSaved, onPreview, onClose, initialQuiz = null }) {
  const [title, setTitle] = useState(initialQuiz?.title || '');
  const [showTitleEditor, setShowTitleEditor] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [quizId, setQuizId] = useState(initialQuiz?.id || null);
  const [saveMessage, setSaveMessage] = useState(null);
  const [questions, setQuestions] = useState(() => {
    if (initialQuiz?.questions?.length > 0) {
      return initialQuiz.questions.map((q) => {
        const type = q.type || 'quiz';
        const expectedCount = getAnswerCount(type);
        const answers =
          q.answers?.map((a) => ({ text: a.text || '', isCorrect: a.isCorrect || false })) || [];
        while (answers.length < expectedCount) {
          answers.push({ text: '', isCorrect: false });
        }
        return {
          type,
          text: q.text || '',
          imageUrl: q.imageUrl || null,
          answers: answers.slice(0, expectedCount),
          timeLimit: q.timeLimit || 20,
        };
      });
    }
    return [createEmptyQuestion('quiz')];
  });

  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const [errors, setErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);

  const [imageFiles, setImageFiles] = useState(() => {
    if (initialQuiz?.questions?.length > 0) {
      return initialQuiz.questions.map(() => null);
    }
    return [null];
  });
  const [imagePreviews, setImagePreviews] = useState(() => {
    if (initialQuiz?.questions?.length > 0) {
      return initialQuiz.questions.map(() => null);
    }
    return [null];
  });

  const carouselRef = useRef(null);
  const titleInputRef = useRef(null);

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleClose = () => {
    if (isDirty) {
      const confirmed = window.confirm('Du har endringar som ikkje er lagra. Er du sikker på at du vil lukke?');
      if (!confirmed) return;
    }
    onClose();
  };

  useEffect(() => {
    if (showTitleEditor && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [showTitleEditor]);

  useEffect(() => {
    if (carouselRef.current) {
      const activeThumb = carouselRef.current.children[activeQuestionIndex];
      if (activeThumb) {
        activeThumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  }, [activeQuestionIndex]);

  const handleAddQuestion = (type = 'quiz') => {
    setIsDirty(true);
    setQuestions([...questions, createEmptyQuestion(type)]);
    setImageFiles([...imageFiles, null]);
    setImagePreviews([...imagePreviews, null]);
    setActiveQuestionIndex(questions.length);
  };

  const handleDuplicateQuestion = (questionIndex) => {
    setIsDirty(true);
    const original = questions[questionIndex];
    const duplicate = {
      type: original.type || 'quiz',
      text: original.text,
      imageUrl: original.imageUrl || null,
      answers: original.answers.map((a) => ({ ...a })),
      timeLimit: original.timeLimit,
    };
    const newQuestions = [...questions];
    newQuestions.splice(questionIndex + 1, 0, duplicate);
    setQuestions(newQuestions);
    const newFiles = [...imageFiles];
    newFiles.splice(questionIndex + 1, 0, imageFiles[questionIndex]);
    setImageFiles(newFiles);
    const newPreviews = [...imagePreviews];
    newPreviews.splice(questionIndex + 1, 0, imagePreviews[questionIndex]);
    setImagePreviews(newPreviews);
    setActiveQuestionIndex(questionIndex + 1);
  };

  const handleMoveQuestion = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    if (toIndex < 0 || toIndex >= questions.length) return;
    setIsDirty(true);
    const move = (arr) => {
      const next = [...arr];
      const [item] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, item);
      return next;
    };
    setQuestions(move(questions));
    setImageFiles(move(imageFiles));
    setImagePreviews(move(imagePreviews));
    setActiveQuestionIndex(toIndex);
  };

  const handleDragStart = (index) => (e) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', String(index));
    } catch (_) {
      // ignore
    }
  };

  const handleDragOver = (index) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => setDragOverIndex(null);

  const handleDrop = (index) => (e) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      handleMoveQuestion(draggedIndex, index);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleRemoveQuestion = (questionIndex) => {
    if (questions.length > 1) {
      setIsDirty(true);
      if (imagePreviews[questionIndex]) {
        URL.revokeObjectURL(imagePreviews[questionIndex]);
      }
      setQuestions(questions.filter((_, index) => index !== questionIndex));
      setImageFiles(imageFiles.filter((_, index) => index !== questionIndex));
      setImagePreviews(imagePreviews.filter((_, index) => index !== questionIndex));
      if (activeQuestionIndex >= questionIndex && activeQuestionIndex > 0) {
        setActiveQuestionIndex(activeQuestionIndex - 1);
      }
    }
  };

  const handleQuestionTextChange = (value) => {
    setIsDirty(true);
    const newQuestions = [...questions];
    newQuestions[activeQuestionIndex].text = value;
    setQuestions(newQuestions);
  };

  const handleTypeChange = (newType) => {
    setIsDirty(true);
    const newQuestions = [...questions];
    const current = newQuestions[activeQuestionIndex];
    if (newType === 'truefalse') {
      current.type = 'truefalse';
      current.answers = [
        { text: 'Sant', isCorrect: true },
        { text: 'Usant', isCorrect: false },
      ];
    } else {
      current.type = 'quiz';
      const existingAnswers = current.answers.map((a) => ({ ...a }));
      while (existingAnswers.length < 4) {
        existingAnswers.push({ text: '', isCorrect: false });
      }
      current.answers = existingAnswers.slice(0, 4);
      if (current.answers[0].text === 'Sant') current.answers[0].text = '';
      if (current.answers[1].text === 'Usant') current.answers[1].text = '';
    }
    setQuestions(newQuestions);
  };

  const handleAnswerTextChange = (answerIndex, value) => {
    setIsDirty(true);
    const newQuestions = [...questions];
    newQuestions[activeQuestionIndex].answers[answerIndex].text = value;
    setQuestions(newQuestions);
  };

  const handleCorrectAnswerChange = (answerIndex) => {
    setIsDirty(true);
    const newQuestions = [...questions];
    newQuestions[activeQuestionIndex].answers = newQuestions[activeQuestionIndex].answers.map(
      (answer, index) => ({ ...answer, isCorrect: index === answerIndex })
    );
    setQuestions(newQuestions);
  };

  const handleTimeLimitChange = (newTimeLimit) => {
    setIsDirty(true);
    const newQuestions = [...questions];
    newQuestions[activeQuestionIndex].timeLimit = newTimeLimit;
    setQuestions(newQuestions);
    setShowTimeDropdown(false);
  };

  const handleImageChange = (file) => {
    if (!file) return;
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setErrors([`Spørsmål ${activeQuestionIndex + 1}: ${validation.error}`]);
      return;
    }
    const tempUrl = URL.createObjectURL(file);
    setCropImageSrc(tempUrl);
    setShowCropModal(true);
  };

  const handleCropComplete = (croppedFile) => {
    setIsDirty(true);
    if (imagePreviews[activeQuestionIndex]) {
      URL.revokeObjectURL(imagePreviews[activeQuestionIndex]);
    }
    if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
    setCropImageSrc(null);
    setShowCropModal(false);
    const newFiles = [...imageFiles];
    const newPreviews = [...imagePreviews];
    newFiles[activeQuestionIndex] = croppedFile;
    newPreviews[activeQuestionIndex] = URL.createObjectURL(croppedFile);
    setImageFiles(newFiles);
    setImagePreviews(newPreviews);
  };

  const handleCropCancel = () => {
    if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
    setCropImageSrc(null);
    setShowCropModal(false);
  };

  const handleRemoveImage = () => {
    setIsDirty(true);
    if (imagePreviews[activeQuestionIndex]) {
      URL.revokeObjectURL(imagePreviews[activeQuestionIndex]);
    }
    const newFiles = [...imageFiles];
    const newPreviews = [...imagePreviews];
    newFiles[activeQuestionIndex] = null;
    newPreviews[activeQuestionIndex] = null;
    setImageFiles(newFiles);
    setImagePreviews(newPreviews);
    const newQuestions = [...questions];
    newQuestions[activeQuestionIndex] = { ...newQuestions[activeQuestionIndex], imageUrl: null };
    setQuestions(newQuestions);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    setSaveMessage(null);

    const quizData = { title: title.trim() || 'Kviss utan tittel', questions };
    const validation = isValidQuiz(quizData);
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    setIsSubmitting(true);
    try {
      let savedQuiz;
      if (quizId) {
        const hasNewImages = imageFiles.some((file) => file !== null);
        let newImageUrls = [];
        if (hasNewImages) {
          newImageUrls = await uploadQuizImages(imageFiles, quizId);
        }
        const questionsWithImages = questions.map((q, index) => ({
          ...q,
          imageUrl: newImageUrls[index] || q.imageUrl || null,
        }));
        await updateQuiz(quizId, { title: quizData.title, questions: questionsWithImages });
        savedQuiz = { id: quizId, title: quizData.title, questions: questionsWithImages };
        if (hasNewImages) {
          imagePreviews.forEach((p) => { if (p) URL.revokeObjectURL(p); });
          setQuestions(questionsWithImages);
          setImageFiles(questionsWithImages.map(() => null));
          setImagePreviews(questionsWithImages.map(() => null));
        }
      } else {
        savedQuiz = await createQuiz(quizData);
        setQuizId(savedQuiz.id);
        const hasImages = imageFiles.some((file) => file !== null);
        if (hasImages) {
          const imageUrls = await uploadQuizImages(imageFiles, savedQuiz.id);
          const updatedQuestions = savedQuiz.questions.map((question, index) => ({
            ...question,
            imageUrl: imageUrls[index] || null,
          }));
          await updateQuiz(savedQuiz.id, { questions: updatedQuestions });
          savedQuiz.questions = updatedQuestions;
          imagePreviews.forEach((p) => { if (p) URL.revokeObjectURL(p); });
          setQuestions(updatedQuestions);
          setImageFiles(updatedQuestions.map(() => null));
          setImagePreviews(updatedQuestions.map(() => null));
        }
      }
      setIsDirty(false);
      setSaveMessage('Lagra!');
      setTimeout(() => setSaveMessage(null), 2000);
      onSaved?.(savedQuiz);
    } catch (error) {
      setErrors([error.message || 'Klarte ikkje å lagre quiz']);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePreview = async () => {
    setErrors([]);
    const quizData = { title: title.trim() || 'Kviss utan tittel', questions };
    const validation = isValidQuiz(quizData);
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    if (isDirty || !quizId) {
      setIsSubmitting(true);
      try {
        let savedQuiz;
        if (quizId) {
          const hasNewImages = imageFiles.some((file) => file !== null);
          let newImageUrls = [];
          if (hasNewImages) {
            newImageUrls = await uploadQuizImages(imageFiles, quizId);
          }
          const questionsWithImages = questions.map((q, index) => ({
            ...q,
            imageUrl: newImageUrls[index] || q.imageUrl || null,
          }));
          await updateQuiz(quizId, { title: quizData.title, questions: questionsWithImages });
          savedQuiz = { id: quizId, title: quizData.title, questions: questionsWithImages };
          if (hasNewImages) {
            imagePreviews.forEach((p) => { if (p) URL.revokeObjectURL(p); });
            setQuestions(questionsWithImages);
            setImageFiles(questionsWithImages.map(() => null));
            setImagePreviews(questionsWithImages.map(() => null));
          }
        } else {
          savedQuiz = await createQuiz(quizData);
          setQuizId(savedQuiz.id);
          const hasImages = imageFiles.some((file) => file !== null);
          if (hasImages) {
            const imageUrls = await uploadQuizImages(imageFiles, savedQuiz.id);
            savedQuiz.questions = savedQuiz.questions.map((question, index) => ({
              ...question,
              imageUrl: imageUrls[index] || null,
            }));
            await updateQuiz(savedQuiz.id, { questions: savedQuiz.questions });
            imagePreviews.forEach((p) => { if (p) URL.revokeObjectURL(p); });
            setQuestions(savedQuiz.questions);
            setImageFiles(savedQuiz.questions.map(() => null));
            setImagePreviews(savedQuiz.questions.map(() => null));
          }
        }
        setIsDirty(false);
        onPreview?.(savedQuiz);
      } catch (error) {
        setErrors([error.message || 'Klarte ikkje å lagre quiz']);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const previewQuiz = {
        id: quizId,
        title: quizData.title,
        questions: questions.map((q) => ({ ...q })),
      };
      onPreview?.(previewQuiz);
    }
  };

  const activeQuestion = questions[activeQuestionIndex];
  const activeAnswerConfig = getAnswerConfig(activeQuestion.type || 'quiz');

  return (
    <form onSubmit={handleSubmit} className="min-h-screen bg-mp-bg-lightgreen flex flex-col font-body">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[rgba(17,73,66,0.12)] px-4 py-3 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={handleClose}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-mp-bg-lightgreen text-mp-darkgreen transition-colors"
          aria-label="Lukk"
        >
          <X size={20} />
        </button>

        {showTitleEditor ? (
          <input
            ref={titleInputRef}
            type="text"
            value={title}
            onChange={(e) => { setIsDirty(true); setTitle(e.target.value); }}
            onBlur={() => setShowTitleEditor(false)}
            onKeyDown={(e) => e.key === 'Enter' && setShowTitleEditor(false)}
            placeholder="Quiz-tittel …"
            className="font-display text-lg bg-transparent border-none text-center focus:outline-none focus:ring-0 text-mp-darkgreen w-44"
          />
        ) : (
          <h1
            className="font-display text-lg cursor-pointer hover:text-mp-magenta transition-colors text-mp-darkgreen truncate max-w-[200px] sm:max-w-md"
            onClick={() => setShowTitleEditor(true)}
            title="Klikk for å endre tittel"
          >
            {title || 'Rediger Quiz'}
          </h1>
        )}

        <div className="flex items-center gap-2">
          {questions.length > 1 && (
            <span className="hidden sm:inline font-display text-mp-stone-500 text-xs tabular-nums">
              {activeQuestionIndex + 1}/{questions.length}
            </span>
          )}
          {saveMessage && (
            <span className="font-display text-mp-teal text-sm">{saveMessage}</span>
          )}
          <button
            type="button"
            onClick={handlePreview}
            disabled={isSubmitting}
            className="hidden sm:inline-flex items-center gap-1.5 bg-white border border-[rgba(17,73,66,0.20)] text-mp-darkgreen px-4 py-2 rounded-full font-display text-sm hover:bg-mp-bg-lightgreen disabled:opacity-50 transition-colors"
          >
            <Eye size={16} />
            Førehandsvis
          </button>
          <button type="submit" disabled={isSubmitting} className="mp-cta !py-2 !px-5 !text-sm disabled:opacity-50">
            {isSubmitting ? 'Lagrar …' : 'Lagre'}
          </button>
        </div>
      </header>

      {/* Question Carousel */}
      <div className="bg-mp-bg-lightgreen pt-4 pb-2">
        <div ref={carouselRef} className="flex overflow-x-auto scrollbar-hide px-4 gap-3 snap-x snap-mandatory">
          {questions.map((q, index) => (
            <div
              key={index}
              draggable
              onDragStart={handleDragStart(index)}
              onDragOver={handleDragOver(index)}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop(index)}
              onDragEnd={handleDragEnd}
              onClick={() => setActiveQuestionIndex(index)}
              className={`snap-center shrink-0 flex flex-col items-center gap-2 cursor-grab active:cursor-grabbing transition-opacity ${
                draggedIndex === index ? 'opacity-30' : index !== activeQuestionIndex ? 'opacity-60 hover:opacity-100' : ''
              }`}
              title="Dra for å endre rekkefølge"
            >
              <div
                className={`w-20 h-14 rounded-lg flex items-center justify-center relative overflow-hidden ${
                  dragOverIndex === index && draggedIndex !== null && draggedIndex !== index
                    ? 'ring-2 ring-mp-lime bg-mp-bg-lightgreen'
                    : index === activeQuestionIndex
                    ? 'ring-2 ring-mp-darkgreen bg-white shadow-mp-2'
                    : 'bg-white border border-[rgba(17,73,66,0.10)]'
                }`}
              >
                {(imagePreviews[index] || q.imageUrl) && (
                  <img src={imagePreviews[index] || q.imageUrl} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
                )}
                <span className={`font-display text-xs relative z-10 ${index === activeQuestionIndex ? 'text-mp-darkgreen' : 'text-mp-stone-500'}`}>
                  Q{index + 1}
                </span>
              </div>
              <div className={`w-1.5 h-1.5 rounded-full ${index === activeQuestionIndex ? 'bg-mp-magenta' : 'bg-transparent'}`} />
            </div>
          ))}

          {/* Add Question Button */}
          <div onClick={() => handleAddQuestion()} className="snap-center shrink-0 flex flex-col items-center gap-2 cursor-pointer">
            <div className="w-20 h-14 rounded-lg border-2 border-dashed border-mp-teal bg-transparent flex items-center justify-center hover:bg-mp-bg-lightgreen transition-colors">
              <Plus size={20} className="text-mp-teal" />
            </div>
            <div className="w-1.5 h-1.5 rounded-full bg-transparent" />
          </div>
        </div>
      </div>

      {/* Main Editor */}
      <main className="flex-1 flex flex-col px-4 pb-36 gap-4 w-full max-w-xl mx-auto">
        {/* Question Actions */}
        <div className="flex justify-end items-center mt-2 gap-3 flex-wrap">
          {questions.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => handleMoveQuestion(activeQuestionIndex, activeQuestionIndex - 1)}
                disabled={activeQuestionIndex === 0}
                className="font-display text-sm text-mp-darkgreen hover:text-mp-magenta inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Flytt spørsmål til venstre"
              >
                <ArrowLeft size={16} />
                Flytt venstre
              </button>
              <button
                type="button"
                onClick={() => handleMoveQuestion(activeQuestionIndex, activeQuestionIndex + 1)}
                disabled={activeQuestionIndex === questions.length - 1}
                className="font-display text-sm text-mp-darkgreen hover:text-mp-magenta inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Flytt spørsmål til høgre"
              >
                Flytt høgre
                <ArrowRight size={16} />
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => handleDuplicateQuestion(activeQuestionIndex)}
            className="font-display text-sm text-mp-teal hover:text-mp-darkgreen inline-flex items-center gap-1"
          >
            <Copy size={16} />
            Dupliser
          </button>
          {questions.length > 1 && (
            <button
              type="button"
              onClick={() => handleRemoveQuestion(activeQuestionIndex)}
              className="font-display text-sm text-mp-magenta hover:text-[#8b0053] inline-flex items-center gap-1"
            >
              <Trash2 size={16} />
              Slett
            </button>
          )}
        </div>

        {/* Question Text */}
        <div className="mt-2">
          <textarea
            value={activeQuestion.text}
            onChange={(e) => handleQuestionTextChange(e.target.value)}
            className="w-full bg-white text-mp-darkgreen font-display text-xl placeholder:text-mp-stone-500 border border-[rgba(17,73,66,0.20)] focus:border-mp-teal focus:ring-3 focus:ring-[rgba(0,137,134,0.18)] rounded-2xl p-4 resize-none shadow-mp-1 min-h-[100px] transition-all outline-none"
            placeholder="Skriv spørsmålet ditt her …"
          />
        </div>

        {/* Media Upload */}
        {(imagePreviews[activeQuestionIndex] || activeQuestion.imageUrl) ? (
          <div className="relative w-full aspect-video rounded-2xl overflow-hidden shadow-mp-1">
            <img
              src={imagePreviews[activeQuestionIndex] || activeQuestion.imageUrl}
              alt="Spørsmålsbilete"
              className="w-full h-full object-cover"
            />
            <button
              type="button"
              onClick={handleRemoveImage}
              className="absolute top-3 right-3 w-9 h-9 bg-mp-darkgreen/85 hover:bg-mp-darkgreen text-white rounded-full flex items-center justify-center transition-colors"
              aria-label="Fjern bilete"
            >
              <X size={18} />
            </button>
          </div>
        ) : (
          <label className="w-full aspect-video rounded-2xl border-2 border-dashed border-mp-teal bg-white flex flex-col items-center justify-center gap-3 cursor-pointer hover:bg-mp-bg-lightgreen transition-colors group">
            <div className="w-14 h-14 rounded-full bg-mp-bg-lightgreen flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <ImageIcon size={26} className="text-mp-teal" />
            </div>
            <p className="font-display text-mp-darkgreen text-sm">
              Trykk for å legge til bilete
            </p>
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={(e) => handleImageChange(e.target.files[0])}
              className="hidden"
            />
          </label>
        )}

        {/* Question Type Selector */}
        <div className="flex gap-2 mt-2">
          <button
            type="button"
            onClick={() => handleTypeChange('quiz')}
            className={`flex-1 py-2.5 rounded-full font-display text-sm flex items-center justify-center gap-2 transition-all border ${
              (activeQuestion.type || 'quiz') === 'quiz'
                ? 'bg-mp-darkgreen text-white border-mp-darkgreen shadow-mp-1'
                : 'bg-white text-mp-darkgreen border-[rgba(17,73,66,0.12)] hover:bg-mp-bg-lightgreen'
            }`}
          >
            <HelpCircle size={16} />
            Quiz
          </button>
          <button
            type="button"
            onClick={() => handleTypeChange('truefalse')}
            className={`flex-1 py-2.5 rounded-full font-display text-sm flex items-center justify-center gap-2 transition-all border ${
              activeQuestion.type === 'truefalse'
                ? 'bg-mp-darkgreen text-white border-mp-darkgreen shadow-mp-1'
                : 'bg-white text-mp-darkgreen border-[rgba(17,73,66,0.12)] hover:bg-mp-bg-lightgreen'
            }`}
          >
            <CheckCheck size={16} />
            Sant eller usant
          </button>
        </div>

        {/* Answer Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          {activeAnswerConfig.map((config, index) => {
            const LucideIcon = LUCIDE_BY_NAME[config.lucideName] || CircleIcon;
            const isCorrect = activeQuestion.answers[index]?.isCorrect;
            return (
              <div key={index} className={`relative group rounded-xl border-2 transition-colors ${isCorrect ? 'border-mp-magenta bg-[rgba(163,0,97,0.06)]' : 'border-transparent'}`}>
                <div className={`absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 ${config.bgColor} ${config.textColor} rounded-lg flex items-center justify-center z-10`}>
                  <LucideIcon size={18} strokeWidth={2.5} />
                </div>
                <input
                  type="text"
                  value={activeQuestion.answers[index]?.text || ''}
                  onChange={(e) => handleAnswerTextChange(index, e.target.value)}
                  readOnly={activeQuestion.type === 'truefalse'}
                  className={`w-full h-14 pl-16 pr-12 rounded-[10px] border border-[rgba(17,73,66,0.12)] focus:border-mp-teal focus:ring-3 focus:ring-[rgba(0,137,134,0.18)] bg-white shadow-mp-1 text-mp-darkgreen font-display placeholder:text-mp-stone-500 transition-all outline-none ${
                    activeQuestion.type === 'truefalse' ? 'cursor-default opacity-80' : ''
                  }`}
                  placeholder={config.placeholder}
                />
                <button
                  type="button"
                  onClick={() => handleCorrectAnswerChange(index)}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${isCorrect ? 'text-mp-magenta' : 'text-mp-stone-300 hover:text-mp-magenta'}`}
                  aria-label={isCorrect ? 'Rett svar' : 'Vel som rett svar'}
                >
                  {isCorrect ? <CheckCircle2 size={22} /> : <CircleIcon size={22} />}
                </button>
              </div>
            );
          })}
        </div>

        {errors.length > 0 && (
          <div className="bg-[rgba(163,0,97,0.06)] border border-[rgba(163,0,97,0.20)] rounded-xl p-4 mt-4">
            <ul className="list-disc list-inside space-y-1">
              {errors.map((error, index) => (
                <li key={index} className="font-body text-mp-magenta text-sm">{error}</li>
              ))}
            </ul>
          </div>
        )}
      </main>

      {/* Bottom Settings Bar */}
      <div className="fixed bottom-0 left-0 w-full bg-white border-t border-[rgba(17,73,66,0.12)] p-4 pb-6 rounded-t-3xl shadow-[0_-4px_20px_rgba(17,73,66,0.10)] z-40">
        <div className="w-12 h-1 bg-mp-stone-200 rounded-full mx-auto mb-4" />

        <div className="flex items-center justify-center gap-4 max-w-xl mx-auto">
          {/* Time Limit */}
          <div className="relative flex-1">
            <div
              onClick={() => setShowTimeDropdown(!showTimeDropdown)}
              className="bg-mp-bg-lightgreen rounded-2xl p-3 flex items-center justify-between cursor-pointer hover:bg-mp-bg-mint transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-mp-teal">
                  <Timer size={20} />
                </div>
                <div className="flex flex-col">
                  <span className="font-display text-[11px] text-mp-teal uppercase tracking-[0.18em]">Tid</span>
                  <span className="font-display text-mp-darkgreen text-sm">{activeQuestion.timeLimit} sek</span>
                </div>
              </div>
              <ChevronDown size={18} className={`text-mp-darkgreen transition-transform ${showTimeDropdown ? 'rotate-180' : ''}`} />
            </div>

            {showTimeDropdown && (
              <div className="absolute bottom-full left-0 w-full mb-2 bg-white border border-[rgba(17,73,66,0.12)] rounded-xl shadow-mp-2 overflow-hidden z-50">
                {[20, 30, 40].map((time) => (
                  <button
                    key={time}
                    type="button"
                    onClick={() => handleTimeLimitChange(time)}
                    className={`w-full px-4 py-3 text-left font-display hover:bg-mp-bg-lightgreen transition-colors ${
                      activeQuestion.timeLimit === time ? 'text-mp-magenta bg-[rgba(163,0,97,0.06)]' : 'text-mp-darkgreen'
                    }`}
                  >
                    {time} sekund
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex-1 bg-mp-bg-lightgreen rounded-2xl p-3 flex items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-mp-fox-rust">
                <Trophy size={20} />
              </div>
              <div className="flex flex-col">
                <span className="font-display text-[11px] text-mp-teal uppercase tracking-[0.18em]">Poeng</span>
                <span className="font-display text-mp-darkgreen text-sm">Standard</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showCropModal && cropImageSrc && (
        <ImageCropModal
          imageSrc={cropImageSrc}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </form>
  );
}
